import React from 'react';
import AppointmentGroupForm from '../AppointmentGroupForm';
import { Form } from 'react-bootstrap';
import { AppointmentGroupState, AppointmentState } from '../types';

/**
 * AppointmentGroupsFormコンポーネント用のPropsです。
 * @interface AppoimentGroupsFormProps
 */
interface AppointmentGroupsFormProps {

  /**
   * 委任事項グループ（複数）
   * @type {AppointmentGroup[]}
   * @memberof AppoimentGroupsFormProps
   */
  appointmentGroups: AppointmentGroupState[];

  /**
   * 委任事項グループ（複数）を更新する関数です。
   * @type {function}
   * @memberof AppoimentGroupsFormProps
   */
  onChange: (appointmentGroups: AppointmentGroupState[]) => void;
}

/**
 * 委任事項グループ（複数）フォームのコンポーネントです。
 * @param {AppointmentGroups} props.appointmentGroups - 委任事項グループ（複数）
 * @param {function} props.onChange - 委任事項の選択状態が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 委任事項グループフォームのJSX要素
 */
const AppointmentGroupsForm: React.FC<AppointmentGroupsFormProps> = ({ appointmentGroups, onChange }) => {

  /**
   * 委任事項（メイン）を更新する関数です。
   * @param {number} appointmentIndex - 委任事項の索引
   * @param {boolean} isChecked - チェック済みフラグ
   * @returns {void}
   */
  const updateMasterAppointmentIsChecked = ( appointmentIndex: number, isChecked: boolean
  ): void => {

    let jurisdcitionIsChecked = false;
    appointmentGroups[appointmentIndex].jurisdictions.forEach((jurisdcition) => {
      jurisdcitionIsChecked = jurisdcition.isChecked ? true : jurisdcitionIsChecked;
    });

    onChange(
      appointmentGroups.map((appointmentGroup, i) => {
        return (
          {
            masterAppointment: {
              appointmentId: appointmentGroup.masterAppointment.appointmentId,
              label: appointmentGroup.masterAppointment.label,
              value: appointmentGroup.masterAppointment.value,
              isChecked: (i === appointmentIndex ? isChecked : appointmentGroup.masterAppointment.isChecked)
            },
            servantAppointments: appointmentGroup.servantAppointments.map((servantAppointment, j) => {
              return (
                {
                  appointmentId: servantAppointment.appointmentId,
                  label: servantAppointment.label,
                  value: servantAppointment.value,
                  isChecked: (i === appointmentIndex && isChecked === false ? isChecked : servantAppointment.isChecked)
                }
              );
            }),
            jurisdictions: appointmentGroup.jurisdictions.map((jurisdcition, j) => {
              return (
                {
                  yonpoCode: jurisdcition.yonpoCode,
                  jurisdictionCode: jurisdcition.jurisdictionCode,
                  appointmentCode: jurisdcition.appointmentCode,
                  label: jurisdcition.label,
                  value: jurisdcition.value,
                  isChecked: (i === appointmentIndex && isChecked === false ? isChecked : (i === appointmentIndex && isChecked === true && jurisdcitionIsChecked === false ? true : jurisdcition.isChecked))
                }
              );
            }),
          }
        )
      })
    );
  }

  /**
   * 委任事項（サブ）を更新する関数です。
   * @param {number} appointmentIndex - 委任事項の索引
   * @param {number} servantAppointmentIndex - 委任事項（サブ）の索引
   * @param {boolean} isChecked - チェック済みフラグ
   * @returns {void}
   */
  const updateServantAppointmentIsChecked = (
    appointmentIndex: number,
    servantAppointmentIndex: number,
    isChecked: boolean
  ): void => {
    onChange(
      appointmentGroups.map((appointment, i) => {
        return (
          {
            masterAppointment: appointment.masterAppointment,
            servantAppointments: appointment.servantAppointments.map((servantAppointment, j) => {
              return (
                {
                  appointmentId: servantAppointment.appointmentId,
                  label: servantAppointment.label,
                  value: servantAppointment.value,
                  isChecked: (i !== appointmentIndex || j !== servantAppointmentIndex ? servantAppointment.isChecked : (appointment.masterAppointment.isChecked === true ? isChecked : false))
                }
              );
            }),
            jurisdictions: appointment.jurisdictions
          }
        );
      })
    )
  }

  /**
   * 法域を更新する関数です。
   * @param {number} appointmentIndex - 委任事項の索引
   * @param {number} jurisdictionIndex - 法域の索引
   * @param {boolean} isChecked - チェック済みフラグ
   * @returns {void}
   */
  const updateJurisdictionIsChecked = (
    appointmentIndex: number,
    jurisdictionIndex: number,
    isChecked: boolean
  ): void => {

    const updatedAppointmentGroups: AppointmentGroupState[] = appointmentGroups.map((appointmentGroup, i) => { 
      return (
        {
          masterAppointment: {
            appointmentId: appointmentGroup.masterAppointment.appointmentId,
            label: appointmentGroup.masterAppointment.label,
            value: appointmentGroup.masterAppointment.value,
            isChecked: (i === appointmentIndex && appointmentGroup.masterAppointment.isChecked === false && isChecked === true ? isChecked : appointmentGroup.masterAppointment.isChecked)
          },
          servantAppointments: appointmentGroup.servantAppointments,
          jurisdictions: appointmentGroup.jurisdictions.map((jurisdiction, j) => {
            return (
              {
                yonpoCode: jurisdiction.yonpoCode,
                jurisdictionCode: jurisdiction.jurisdictionCode,
                appointmentCode: jurisdiction.appointmentCode,
                label: jurisdiction.label,
                value: jurisdiction.value,
                isChecked: (i === appointmentIndex && j === jurisdictionIndex ? isChecked : jurisdiction.isChecked)
              }
            )
          })
        }
      )
    });

    const jurisdcitionIsCheckeds: boolean[] = [];
    updatedAppointmentGroups[appointmentIndex].jurisdictions.forEach((jurisdcition) => {
      jurisdcitionIsCheckeds.push(jurisdcition.isChecked);
    });

    const jurisdcitionIsChecked: boolean = jurisdcitionIsCheckeds.some(item => item);

    onChange(
      updatedAppointmentGroups.map((appointmentGroup, i) => { 
        return (
          {
            masterAppointment: {
              appointmentId: appointmentGroup.masterAppointment.appointmentId,
              label: appointmentGroup.masterAppointment.label,
              value: appointmentGroup.masterAppointment.value,
              isChecked: (i === appointmentIndex ? jurisdcitionIsChecked : appointmentGroup.masterAppointment.isChecked)
            },
            servantAppointments: appointmentGroup.servantAppointments.map((servantAppointment) => {
              return (
                {
                  appointmentId: servantAppointment.appointmentId,
                  label: servantAppointment.label,
                  value: servantAppointment.value,
                  isChecked: (i === appointmentIndex && jurisdcitionIsChecked === false ? false : servantAppointment.isChecked)
                }
              );
            }),
            jurisdictions: appointmentGroup.jurisdictions
          }
        )
      })
    );
  }

  return (
    <>
      <Form.Label>
        委任事項
        <span className="text-small text-secondary ms-3">※任意</span>
      </Form.Label>
      {
        appointmentGroups.map((appointmentGroup, appointmentGroupIndex) => {
          return (
            <div key={appointmentGroupIndex} className="mb-4">
              <AppointmentGroupForm
                appointmentIndex={appointmentGroupIndex}
                appointmentGroup={appointmentGroup}
                updateMasterAppointmentIsChecked={updateMasterAppointmentIsChecked}
                updateServantAppointmentIsChecked={updateServantAppointmentIsChecked}
                updateJurisdictionIsChecked={updateJurisdictionIsChecked}
              />
            </div>
          );
        })
      }
    </>
  );
}

export default AppointmentGroupsForm;